import React, { useState, useEffect, useCallback } from 'react';
import i18n from '@/settings/i18n';
import { useTranslation } from 'react-i18next';

import { AdminRole, Affiliation } from '@/types/affiliation';
import { useAppSelector } from '@/store';
import { authStore } from '@/store/auth';
import { affiliationsAdminApi, affiliationsApi } from '@/services/api';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';

import { MembershipList } from '../Presentation';
import { pages } from '@/settings/pages';

export const MembershipListContainer: React.FC = () => {
  const { apiErrorHandler } = useApiErrorHandler();
  const { flashNotice } = useFlashMessenger();
  const organizationId = useAppSelector(
    authStore.selectors.organizationIdSelector,
  );
  const me = useAppSelector(authStore.selectors.meSelector);
  const gloplaId = useAppSelector(authStore.selectors.gloplaIdSelector);

  const isManager = useAppSelector(authStore.selectors.isManagerSelector);

  const [isLoading, setIsLoading] = useState(false);
  const [affiliations, setAffiliations] = useState<Affiliation[]>([]);

  const { t } = useTranslation();

  useEffect(() => {
    const fetchAffiliations = async () => {
      setIsLoading(true);
      try {
        const affiliations =
          await affiliationsApi.getAffiliationsOfOrganization({
            data: { organizationId },
          });
        setAffiliations(affiliations);
      } catch (err) {
        apiErrorHandler(err);
      }
      setIsLoading(false);
    };

    fetchAffiliations();
  }, [apiErrorHandler, organizationId]);

  const onClickDelete = useCallback(
    async (adminId: number) => {
      try {
        await affiliationsAdminApi.delete({
          data: {
            adminId,
            organizationId,
          },
        });
        const newAffiliations = affiliations.filter(
          (affiliation) => affiliation.admin.id !== adminId,
        );
        setAffiliations(newAffiliations);
        flashNotice(i18n.t('text.membership.delete.done'));
      } catch (err) {
        apiErrorHandler(err, gloplaId, pages.membership.index);
      }
    },
    [affiliations, apiErrorHandler, flashNotice, gloplaId, organizationId],
  );

  const onClickUpdateRole = useCallback(
    async (adminId: number, role: AdminRole) => {
      try {
        const updatedAffiliation = await affiliationsAdminApi.updateRole({
          data: {
            role,
            adminId,
            organizationId,
          },
        });
        const newAffiliations = affiliations.map((affiliation) =>
          affiliation.admin.id === updatedAffiliation.admin.id
            ? updatedAffiliation
            : affiliation,
        );
        setAffiliations(newAffiliations);
        flashNotice(t('text.membership.updateRole.done'));
      } catch (err) {
        apiErrorHandler(err, gloplaId, pages.membership.index);
      }
    },
    [affiliations, apiErrorHandler, flashNotice, gloplaId, organizationId, t],
  );

  const hasManagerInOrganization = !!affiliations.find(
    (o) => o.role === 'manager',
  );

  if (isLoading) {
    return null;
  }

  const childProps = {
    affiliations,
    gloplaId,
    me,
    hasManagerInOrganization,
    isManager,
    onClickDelete,
    onClickUpdateRole,
  };
  return <MembershipList {...childProps} />;
};
